import { useEffect, useMemo, useState } from "react";
import { apiGet } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { useToast } from "../context/ToastContext";
import DataTable from "../components/shared/DataTable";
import { formatDateTime } from "../utils/dateTime";

const PAGE_SIZE = 20;

function formatDate(value) {
  return formatDateTime(value);
}

function truncate(value, max = 72) {
  if (value == null || value === "") return "--";
  const text = String(value).replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
}

function prettyJson(value) {
  if (!value) return "--";
  try {
    return truncate(JSON.stringify(value), 120);
  } catch {
    return truncate(String(value), 120);
  }
}

export default function AdminAnalytics() {
  const { showError } = useToast();
  const [activeTab, setActiveTab] = useState("events");

  const [eventRows, setEventRows] = useState([]);
  const [eventsPagination, setEventsPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: PAGE_SIZE });
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventFilters, setEventFilters] = useState({
    search: "",
    eventName: "",
    country: "",
    deviceType: "",
    dateFrom: "",
    dateTo: "",
  });
  const [eventOptions, setEventOptions] = useState({ eventNames: [] });

  const [benchmarkRows, setBenchmarkRows] = useState([]);
  const [benchmarksPagination, setBenchmarksPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: PAGE_SIZE });
  const [benchmarksLoading, setBenchmarksLoading] = useState(false);
  const [benchmarkFilters, setBenchmarkFilters] = useState({
    search: "",
    industry: "",
    dateFrom: "",
    dateTo: "",
    sortBy: "last_audit_desc",
  });
  const [benchmarkOptions, setBenchmarkOptions] = useState({ industries: [] });

  const loadEvents = async (page = 1) => {
    setEventsLoading(true);
    try {
      const { data } = await apiGet("/api/admin/analytics/events", {
        page,
        limit: PAGE_SIZE,
        ...eventFilters,
      });
      setEventRows(Array.isArray(data?.data) ? data.data : []);
      setEventsPagination(data?.pagination || { page, totalPages: 1, total: 0, limit: PAGE_SIZE });
      setEventOptions(data?.filters || { eventNames: [] });
    } catch (err) {
      showError(parseApiError(err, "Failed to load analytics events"));
    } finally {
      setEventsLoading(false);
    }
  };

  const loadBenchmarks = async (page = 1) => {
    setBenchmarksLoading(true);
    try {
      const { data } = await apiGet("/api/admin/analytics/benchmarks", {
        page,
        limit: PAGE_SIZE,
        ...benchmarkFilters,
      });
      setBenchmarkRows(Array.isArray(data?.data) ? data.data : []);
      setBenchmarksPagination(data?.pagination || { page, totalPages: 1, total: 0, limit: PAGE_SIZE });
      setBenchmarkOptions(data?.filters || { industries: [] });
    } catch (err) {
      showError(parseApiError(err, "Failed to load domain benchmarks"));
    } finally {
      setBenchmarksLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "events") {
      loadEvents(1);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "benchmarks") {
      loadBenchmarks(1);
    }
  }, [activeTab]);

  const eventSummary = useMemo(() => {
    const totalDuration = eventRows.reduce((sum, row) => sum + Number(row.duration_ms || 0), 0);
    const cacheHits = eventRows.filter((row) => row.cache_hit === true).length;
    return {
      totalDuration,
      cacheHits,
    };
  }, [eventRows]);

  return (
    <div className="page page-admin-wide">
      <div className="section-actions" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Admin Analytics</h1>
          <p className="dashboard-lead">
            Inspect captured product events and domain benchmark snapshots from the analytics tables.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className={`btn ${activeTab === "events" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setActiveTab("events")}
          >
            Events
          </button>
          <button
            type="button"
            className={`btn ${activeTab === "benchmarks" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setActiveTab("benchmarks")}
          >
            Benchmarks
          </button>
        </div>
      </div>

      {activeTab === "events" ? (
        <>
          <div className="dashboard-kpis" style={{ marginBottom: 20 }}>
            <div className="card dashboard-kpi">
              <div className="dashboard-kpi-label">Visible Events</div>
              <div className="dashboard-kpi-value">{eventsPagination.total ?? 0}</div>
            </div>
            <div className="card dashboard-kpi">
              <div className="dashboard-kpi-label">Cache Hits On Page</div>
              <div className="dashboard-kpi-value">{eventSummary.cacheHits}</div>
            </div>
            <div className="card dashboard-kpi">
              <div className="dashboard-kpi-label">Duration Sum</div>
              <div className="dashboard-kpi-value">{eventSummary.totalDuration} ms</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div className="filter-group">
              <div>
                <label className="filter-label">Search</label>
                <input
                  className="input"
                  value={eventFilters.search}
                  onChange={(e) => setEventFilters((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Event, URL, country, details..."
                />
              </div>
              <div>
                <label className="filter-label">Event Name</label>
                <select
                  className="input"
                  value={eventFilters.eventName}
                  onChange={(e) => setEventFilters((prev) => ({ ...prev, eventName: e.target.value }))}
                >
                  <option value="">All Events</option>
                  {eventOptions.eventNames.map((item) => (
                    <option key={item.event_name} value={item.event_name}>
                      {item.event_name} ({item.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="filter-label">Country</label>
                <input
                  className="input"
                  value={eventFilters.country}
                  onChange={(e) => setEventFilters((prev) => ({ ...prev, country: e.target.value.toUpperCase() }))}
                  placeholder="IN, US..."
                />
              </div>
              <div>
                <label className="filter-label">Device</label>
                <input
                  className="input"
                  value={eventFilters.deviceType}
                  onChange={(e) => setEventFilters((prev) => ({ ...prev, deviceType: e.target.value }))}
                  placeholder="desktop, mobile..."
                />
              </div>
              <div>
                <label className="filter-label">From</label>
                <input
                  type="date"
                  className="input"
                  value={eventFilters.dateFrom}
                  onChange={(e) => setEventFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                />
              </div>
              <div>
                <label className="filter-label">To</label>
                <input
                  type="date"
                  className="input"
                  value={eventFilters.dateTo}
                  onChange={(e) => setEventFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-primary" onClick={() => loadEvents(1)} disabled={eventsLoading}>
                {eventsLoading ? "Loading..." : "Apply Filters"}
              </button>
            </div>
          </div>

          {eventsLoading ? <div className="loading">Loading analytics events...</div> : null}
          {!eventsLoading && (
            <DataTable
              headers={[
                { key: "event_name", label: "EVENT", render: (row) => <strong>{row.event_name}</strong> },
                { key: "url", label: "URL", render: (row) => truncate(row.url, 54) },
                { key: "device_type", label: "DEVICE", render: (row) => row.device_type || "--" },
                { key: "country", label: "COUNTRY", render: (row) => row.country || "--" },
                { key: "score", label: "SCORE", render: (row) => row.score ?? "--" },
                { key: "duration_ms", label: "DURATION", render: (row) => row.duration_ms != null ? `${row.duration_ms} ms` : "--" },
                { key: "cache_hit", label: "CACHE", render: (row) => row.cache_hit === true ? "Hit" : row.cache_hit === false ? "Miss" : "--" },
                { key: "details", label: "DETAILS", render: (row) => prettyJson(row.details) },
                { key: "created_at", label: "CREATED", render: (row) => formatDate(row.created_at) },
              ]}
              data={eventRows}
              keyExtractor={(row) => row.id}
            />
          )}

          {eventsPagination.totalPages > 1 ? (
            <div className="pagination" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-ghost" disabled={eventsPagination.page <= 1} onClick={() => loadEvents(eventsPagination.page - 1)}>
                {"<- Prev"}
              </button>
              <span className="pagination-info">
                Page {eventsPagination.page} of {eventsPagination.totalPages} - {eventsPagination.total} total
              </span>
              <button type="button" className="btn btn-ghost" disabled={eventsPagination.page >= eventsPagination.totalPages} onClick={() => loadEvents(eventsPagination.page + 1)}>
                {"Next ->"}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {activeTab === "benchmarks" ? (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="filter-group">
              <div>
                <label className="filter-label">Search</label>
                <input
                  className="input"
                  value={benchmarkFilters.search}
                  onChange={(e) => setBenchmarkFilters((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Domain or industry..."
                />
              </div>
              <div>
                <label className="filter-label">Industry</label>
                <select
                  className="input"
                  value={benchmarkFilters.industry}
                  onChange={(e) => setBenchmarkFilters((prev) => ({ ...prev, industry: e.target.value }))}
                >
                  <option value="">All Industries</option>
                  {benchmarkOptions.industries.map((item) => (
                    <option key={item.industry} value={item.industry}>
                      {item.industry} ({item.count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="filter-label">From</label>
                <input
                  type="date"
                  className="input"
                  value={benchmarkFilters.dateFrom}
                  onChange={(e) => setBenchmarkFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                />
              </div>
              <div>
                <label className="filter-label">To</label>
                <input
                  type="date"
                  className="input"
                  value={benchmarkFilters.dateTo}
                  onChange={(e) => setBenchmarkFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                />
              </div>
              <div>
                <label className="filter-label">Sort</label>
                <select
                  className="input"
                  value={benchmarkFilters.sortBy}
                  onChange={(e) => setBenchmarkFilters((prev) => ({ ...prev, sortBy: e.target.value }))}
                >
                  <option value="last_audit_desc">Latest Audit</option>
                  <option value="avg_score_desc">Highest Avg. Score</option>
                  <option value="avg_score_asc">Lowest Avg. Score</option>
                  <option value="sample_size_desc">Largest Sample</option>
                  <option value="domain_asc">Domain A-Z</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-primary" onClick={() => loadBenchmarks(1)} disabled={benchmarksLoading}>
                {benchmarksLoading ? "Loading..." : "Apply Filters"}
              </button>
            </div>
          </div>

          {benchmarksLoading ? <div className="loading">Loading benchmarks...</div> : null}
          {!benchmarksLoading && (
            <DataTable
              headers={[
                { key: "domain", label: "DOMAIN", render: (row) => <strong>{row.domain}</strong> },
                { key: "industry", label: "INDUSTRY", render: (row) => row.industry || "--" },
                { key: "audit_count", label: "AUDITS", render: (row) => row.audit_count },
                { key: "sample_size", label: "SAMPLE", render: (row) => row.sample_size },
                { key: "last_score", label: "LAST SCORE", render: (row) => row.last_score },
                { key: "avg_score", label: "AVG. SCORE", render: (row) => row.avg_score ?? "--" },
                { key: "last_audit_at", label: "LAST AUDIT", render: (row) => formatDate(row.last_audit_at) },
              ]}
              data={benchmarkRows}
              keyExtractor={(row) => row.domain}
            />
          )}

          {benchmarksPagination.totalPages > 1 ? (
            <div className="pagination" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-ghost" disabled={benchmarksPagination.page <= 1} onClick={() => loadBenchmarks(benchmarksPagination.page - 1)}>
                {"<- Prev"}
              </button>
              <span className="pagination-info">
                Page {benchmarksPagination.page} of {benchmarksPagination.totalPages} - {benchmarksPagination.total} total
              </span>
              <button type="button" className="btn btn-ghost" disabled={benchmarksPagination.page >= benchmarksPagination.totalPages} onClick={() => loadBenchmarks(benchmarksPagination.page + 1)}>
                {"Next ->"}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
